// Evens out product-card, complementary-card, cart upsell and cart line photos with a plain background: the tile takes the photo's own background colour
// (white stays white) and the product is scaled and centred to the same fill on every card.
// Photos with a busy background, lifestyle shots and images that can't be read are left untouched.
(() => {
  const SELECTOR = [
    '.product-card__media.media--product:not(.is-lifestyle) .product-card__image:not(.is-lifestyle)',
    '.complementary-card__media.media--product:not(.is-lifestyle) > img:not(.is-lifestyle)',
    '.cart-upsells__media.media--product:not(.is-lifestyle) > img:not(.is-lifestyle)',
    '.cart-line__media.media--product > img',
  ].join(', ');
  const FRAME = '.product-card__media, .complementary-card__media, .cart-upsells__media, .cart-line__media';
  const SAMPLE = 72;
  const FILL = 0.78;
  const MAX_SCALE = 1.6;
  const MIN_SCALE = 0.75;
  const CACHE_KEY = 'convert:media-fit:v4';
  const cache = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
    } catch {
      return {};
    }
  })();
  let saveTimer = 0;
  const done = new WeakSet();

  const keyFor = (img) => (img.currentSrc || img.src || '').replace(/^https?:/, '').replace(/[?&](width|height|crop)=[^&]*/g, '');

  function save() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      } catch {
        /* quota */
      }
    }, 400);
  }

  function sampleUrl(img) {
    const url = new URL(img.currentSrc || img.src, location.href);
    url.searchParams.set('width', String(SAMPLE * 2));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.href;
  }

  function load(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  // Background = median of the border pixels; it only counts when the border is close to one flat colour.
  function analyse(image) {
    const ratio = image.naturalWidth / image.naturalHeight || 1;
    const width = ratio >= 1 ? SAMPLE : Math.max(8, Math.round(SAMPLE * ratio));
    const height = ratio >= 1 ? Math.max(8, Math.round(SAMPLE / ratio)) : SAMPLE;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    const at = (x, y) => (y * width + x) * 4;
    const border = [];
    for (let x = 0; x < width; x += 1) border.push(at(x, 0), at(x, height - 1));
    for (let y = 1; y < height - 1; y += 1) border.push(at(0, y), at(width - 1, y));
    const median = (channel) => {
      const values = border.map((index) => data[index + channel]).sort((a, b) => a - b);
      return values[values.length >> 1];
    };
    const bg = [median(0), median(1), median(2)];
    const distance = (index) =>
      Math.max(Math.abs(data[index] - bg[0]), Math.abs(data[index + 1] - bg[1]), Math.abs(data[index + 2] - bg[2]));
    const isFlat = (index) => data[index + 3] > 200 && distance(index) <= 18;
    const flat = border.filter(isFlat).length / border.length;
    // A product that runs off one or two edges (shorts, trousers) still counts when the other sides are plain.
    const side = (indexes) => indexes.filter(isFlat).length / indexes.length;
    const top = [];
    const bottom = [];
    const left = [];
    const right = [];
    for (let x = 0; x < width; x += 1) top.push(at(x, 0)), bottom.push(at(x, height - 1));
    for (let y = 0; y < height; y += 1) left.push(at(0, y)), right.push(at(width - 1, y));
    const plainSides = [top, bottom, left, right].filter((indexes) => side(indexes) >= 0.9).length;
    if (flat < 0.85 && !(flat >= 0.6 && plainSides >= 2)) return null;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = at(x, y);
        if (data[index + 3] > 40 && distance(index) > 12) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return null;
    const white = bg.every((value) => value >= 251);
    return {
      bg: white ? '#ffffff' : `rgb(${bg.join(',')})`,
      ratio,
      box: [minX / width, minY / height, (maxX + 1) / width, (maxY + 1) / height],
    };
  }

  // Applied in one animation frame: every frame is measured first, then every image is written,
  // so a grid or re-rendered cart of photos costs one layout instead of one per photo.
  const queued = new Map();
  const retries = new WeakMap();
  let frameRequest = 0;

  function apply(img, result) {
    if (!result) return;
    queued.set(img, result);
    if (!frameRequest) frameRequest = requestAnimationFrame(flush);
  }

  function flush() {
    frameRequest = 0;
    const batch = [...queued];
    queued.clear();
    const measured = batch.map(([img, result]) => {
      const frame = img.isConnected ? img.closest(FRAME) : null;
      const rect = frame?.getBoundingClientRect();
      const pad = rect?.width ? parseFloat(getComputedStyle(img).paddingLeft) || 0 : 0;
      return { img, result, frame, rect, pad };
    });
    measured.forEach(({ img, result, frame, rect, pad }) => {
      if (!frame) return;
      // Hidden when it arrived (closed cart drawer): fit it once it shows.
      if (!rect.width || !rect.height) {
        const tries = (retries.get(img) || 0) + 1;
        retries.set(img, tries);
        if (observer && tries <= 3) {
          done.delete(img);
          observer.observe(img);
        }
        return;
      }
      write(img, result, frame, rect, pad);
    });
  }

  function write(img, result, frame, rect, pad) {
    const W = rect.width;
    const H = rect.height;
    const areaW = W - pad * 2;
    const areaH = H - pad * 2;
    const drawW = Math.min(areaW, areaH * result.ratio);
    const drawH = drawW / result.ratio;
    const drawX = pad + (areaW - drawW) / 2;
    const drawY = pad + (areaH - drawH) / 2;
    const [x0, y0, x1, y1] = result.box;
    const objW = (x1 - x0) * drawW;
    const objH = (y1 - y0) * drawH;
    const objCX = drawX + ((x0 + x1) / 2) * drawW;
    const objCY = drawY + ((y0 + y1) / 2) * drawH;
    const maxScale = Number(frame.dataset.fitMax) || MAX_SCALE;
    const scale = Math.max(MIN_SCALE, Math.min(maxScale, Math.min((FILL * W) / objW, (FILL * H) / objH)));
    const tx = -scale * (objCX - W / 2);
    const ty = -scale * (objCY - H / 2);
    img.style.setProperty('--fit-x', `${((tx / W) * 100).toFixed(3)}%`);
    img.style.setProperty('--fit-y', `${((ty / H) * 100).toFixed(3)}%`);
    img.style.setProperty('--fit-scale', scale.toFixed(4));
    img.style.setProperty('--fit-bg', result.bg);
    img.classList.add('is-fit');
    const primary = img.classList.contains('product-card__image--primary') || img === frame.querySelector('img');
    if (primary || !frame.style.getPropertyValue('--fit-bg')) {
      frame.style.setProperty('--fit-bg', result.bg);
      frame.classList.add('is-fit');
    }
    if (scale > 1.05 && img.sizes) img.sizes = `${Math.ceil(W * scale)}px`;
  }

  async function fit(img) {
    if (done.has(img)) return;
    done.add(img);
    const cached = cache[keyFor(img)];
    if (cached !== undefined) {
      apply(img, cached);
      return;
    }
    if (!img.complete) await new Promise((resolve) => img.addEventListener('load', resolve, { once: true }));
    const key = keyFor(img);
    if (!key) return;
    if (key in cache) {
      apply(img, cache[key]);
      return;
    }
    try {
      const result = analyse(await load(sampleUrl(img)));
      cache[key] = result;
      save();
      apply(img, result);
    } catch {
      /* cross-origin or decode failure: keep the default tile */
    }
  }

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              fit(entry.target);
            }),
          { rootMargin: '400px 0px' }
        )
      : null;

  function scan(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach((img) => {
      if (done.has(img)) return;
      if (keyFor(img) in cache) fit(img);
      else if (observer) observer.observe(img);
      else fit(img);
    });
  }

  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach((node) => node.nodeType === 1 && scan(node));
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
})();
